/* ============================================================
   V4 — Postholder Planner
   Ported from the scaffold. A-14: the dead `detail.id = ...` re-assign
   is gone; the detail container keeps a stable id set once.
   ============================================================ */

import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { PrintButton } from '../../components/PrintButton.js';
import { setPageMeasure } from '../../shared/measure.js';

const POSTHOLDERS = {
  'road-freight': {
    name: 'Road Freight Operator',
    required: [
      {
        role: 'Managing Director',
        qualification: 'Degree plus 10 years in logistics',
        regulatory: 'May face an NTSA fit-and-proper assessment',
        count: '1'
      },
      {
        role: 'Operations Manager',
        qualification: 'Diploma or degree in logistics or transport',
        regulatory: 'Named on the NTSA Commercial Vehicle Operator Licence',
        count: '1'
      },
      {
        role: 'Fleet Manager',
        qualification: 'Automotive engineering or equivalent',
        regulatory: 'Accountable for maintenance compliance',
        count: '1'
      },
      {
        role: 'Safety Officer',
        qualification: 'NEBOSH or equivalent safety certification',
        regulatory: 'Required for the NTSA operator licence',
        count: '1'
      },
      {
        role: 'Compliance Officer',
        qualification: 'Legal or paralegal background preferred',
        regulatory: 'KRA, NTSA and KEBS liaison',
        count: '1'
      },
      {
        role: 'Finance Manager',
        qualification: 'CPA(K) or ACCA',
        regulatory: 'Financial reporting and tax compliance',
        count: '1'
      },
      {
        role: 'Drivers',
        qualification: 'Valid commercial licence class CE or C1E',
        regulatory: 'NTSA medical fitness and defensive driving certification',
        count: 'Per fleet size'
      }
    ],
    recommended: [
      { role: 'Business Development Manager', purpose: 'Client acquisition and retention' },
      { role: 'Workshop Supervisor', purpose: 'In-house maintenance capability' },
      { role: 'Systems Administrator', purpose: 'Telematics and fleet management software' },
      { role: 'Training Coordinator', purpose: 'Driver refreshers and safety programmes' }
    ]
  },

  'warehouse-icd': {
    name: 'Warehouse / ICD Operator',
    required: [
      {
        role: 'Managing Director',
        qualification: 'Degree plus supply chain experience',
        regulatory: 'Named on the KRA bonded warehouse licence',
        count: '1'
      },
      {
        role: 'Warehouse Manager',
        qualification: 'Diploma in warehousing or logistics',
        regulatory: 'Accountable for stock accuracy and security',
        count: '1'
      },
      {
        role: 'Customs & Compliance Manager',
        qualification: 'KRA-licensed customs broker',
        regulatory: 'KRA customs agent licence is mandatory',
        count: '1'
      },
      {
        role: 'Security Manager',
        qualification: 'Security management certification',
        regulatory: 'CCTV, perimeter and access control oversight',
        count: '1'
      },
      {
        role: 'Finance Manager',
        qualification: 'CPA(K) or ACCA',
        regulatory: 'Bond reconciliation and duty payments',
        count: '1'
      },
      {
        role: 'Inventory Controller',
        qualification: 'Diploma in supply chain',
        regulatory: 'KRA monthly stock returns',
        count: '1'
      },
      {
        role: 'Equipment Operators',
        qualification: 'Certified forklift operators',
        regulatory: 'Occupational safety compliance',
        count: 'Per volume'
      }
    ],
    recommended: [
      { role: 'IT Systems Manager', purpose: 'WMS and iCMS integration' },
      { role: 'Quality Assurance Officer', purpose: 'KEBS compliance and damage prevention' },
      { role: 'Client Services Manager', purpose: 'Shipper relationship management' }
    ]
  },

  'clearing-agent': {
    name: 'Customs Clearing Agent',
    required: [
      {
        role: 'Managing Director',
        qualification: 'Degree plus customs clearance experience',
        regulatory: 'KRA fit-and-proper assessment',
        count: '1'
      },
      {
        role: 'Licensed Customs Broker',
        qualification: 'East African Customs and Freight Forwarding Practising Certificate',
        regulatory: 'KRA customs agent licence — mandatory',
        count: 'At least 1 per office'
      },
      {
        role: 'Operations Coordinator',
        qualification: 'Diploma in logistics',
        regulatory: 'Declaration processing and client liaison',
        count: '1'
      },
      {
        role: 'Documentation Officer',
        qualification: 'Proficiency in trade documentation',
        regulatory: 'Bill of lading, CoC and IDF accuracy',
        count: '1'
      },
      {
        role: 'Finance Officer',
        qualification: 'Accounting diploma',
        regulatory: 'Duty payment and client invoicing',
        count: '1'
      }
    ],
    recommended: [
      { role: 'Business Development Manager', purpose: 'Shipper and importer acquisition' },
      { role: 'Regulatory Affairs Officer', purpose: 'EAC and AfCFTA policy tracking' },
      { role: 'IT Support', purpose: 'iCMS and TradeNet administration' }
    ]
  }
};

export function OrganogramPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>Postholder Planner</h1>
      <p>Required and recommended personnel by operation type, with the regulatory reason for each.</p>
    </header>
    <div id="organogram-body"></div>
  `;

  renderSelector(page.querySelector('#organogram-body'));
  return page;
}

function renderSelector(container) {
  setPageMeasure(container, 'wide');
  container.replaceChildren();
  const list = document.createElement('div');
  /* tool-grid, not stack: these are three or four sibling choices, and
     stacking them put one 640px-wide card per row with 740px of empty
     page beside it. The same cards as an auto-fill grid are one row. */
  list.className = 'tool-grid';

  for (const [key, org] of Object.entries(POSTHOLDERS)) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'card card-link';
    card.innerHTML = html`
      <div class="card-stack">
      <h3 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-1);">${org.name}</h3>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary);">
        <span class="num">${org.required.length}</span> required ·
        <span class="num">${org.recommended.length}</span> recommended
      </p>
      </div>
    `;
    card.addEventListener('click', () => renderDetail(container, key));
    list.appendChild(card);
  }

  container.appendChild(list);
}

function renderDetail(container, key) {
  setPageMeasure(container, 'reading');
  const org = POSTHOLDERS[key];
  container.replaceChildren();

  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn-ghost no-print';
  back.style.marginBottom = 'var(--ca-space-4)';
  back.innerHTML = `${icon('left', 16)}<span>All operation types</span>`;
  back.addEventListener('click', () => renderSelector(container));
  container.appendChild(back);

  const detail = document.createElement('div');
  detail.id = 'organogram-detail';
  detail.innerHTML = html`
    <h2 style="font-size: var(--ca-text-lg); margin-bottom: var(--ca-space-1);">${org.name}</h2>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-5);">
      Personnel requirements for licensing and operations
    </p>

    <section class="card avoid-break" style="margin-bottom: var(--ca-space-4);">
      <h3 style="font-size: var(--ca-text-sm); color: var(--ca-danger); margin-bottom: var(--ca-space-3);">
        Required posts (<span class="num">${org.required.length}</span>)
      </h3>
      <div class="stack" style="gap: var(--ca-space-3);">
        ${org.required.map(
          (post) => html`
            <div style="padding: var(--ca-space-3); border: 1px solid var(--ca-border); border-radius: var(--ca-radius-md);">
              <div class="row" style="margin-bottom: var(--ca-space-2);">
                <span style="flex:1; font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">
                  ${post.role}
                </span>
                <span class="badge" style="background: var(--ca-surface-sunk); color: var(--ca-text-tertiary);">
                  ${post.count}
                </span>
              </div>
              <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); line-height: var(--ca-leading-snug);">
                <div><strong style="font-weight: var(--ca-weight-medium);">Qualification:</strong> ${post.qualification}</div>
                <div style="margin-top: 2px;"><strong style="font-weight: var(--ca-weight-medium);">Regulatory:</strong> ${post.regulatory}</div>
              </div>
            </div>
          `
        )}
      </div>
    </section>

    <section class="card avoid-break" style="margin-bottom: var(--ca-space-4);">
      <h3 style="font-size: var(--ca-text-sm); color: var(--ca-chart-2); margin-bottom: var(--ca-space-3);">
        Recommended posts (<span class="num">${org.recommended.length}</span>)
      </h3>
      <div class="stack" style="gap: var(--ca-space-2);">
        ${org.recommended.map(
          (post) => html`
            <div style="padding: var(--ca-space-3); border: 1px solid var(--ca-border); border-radius: var(--ca-radius-md);">
              <div style="font-size: var(--ca-text-sm); font-weight: var(--ca-weight-medium);">${post.role}</div>
              <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">${post.purpose}</div>
            </div>
          `
        )}
      </div>
    </section>
  `;

  container.appendChild(detail);

  detail.appendChild(
    PrintButton({
      targetId: 'organogram-detail',
      title: `${org.name} — Postholder Plan`,
      subtitle: 'Required and recommended personnel'
    })
  );
}
